import { z } from 'zod';
import { validateIdentificationNumber } from '../nigeria.js';
import { DocumentType, EmploymentStatus, Gender, KycStatus, MemberStatus } from '../enums.js';
import { accountNumberSchema, bvnSchema, dateOfBirthSchema, emailSchema, nameSchema, optionalNameSchema, phoneSchema, searchSchema, uuidSchema, } from './common.js';
export const nextOfKinSchema = z.object({
    fullName: nameSchema,
    relationship: z.string().trim().min(2).max(50),
    phone: phoneSchema,
    email: z.union([emailSchema, z.literal('')]).optional(),
    address: z.string().trim().min(5).max(255),
});
/**
 * A member's own profile.
 *
 * This is the single record of who someone is. KYC verifies it and holds the
 * documents; it does not keep a second copy of the same facts.
 *
 * Every field here is optional except name and phone, so a member can fill the
 * profile in over time rather than being blocked by one unknown.
 */
export const updateProfileSchema = z.object({
    // --- Identity ---
    firstName: nameSchema,
    middleName: optionalNameSchema,
    lastName: nameSchema,
    dateOfBirth: z.union([dateOfBirthSchema, z.literal('')]).optional(),
    gender: z.union([z.nativeEnum(Gender), z.literal('')]).optional(),
    // --- Contact ---
    phone: phoneSchema,
    alternatePhone: z.union([phoneSchema, z.literal('')]).optional(),
    // --- Address ---
    residentialAddress: z.string().trim().max(255).optional(),
    city: z.string().trim().max(80).optional(),
    state: z.string().trim().max(80).optional(),
    lga: z.string().trim().max(80).optional(),
    // --- Membership ---
    /// Free text rather than an enum: the association can open a new chapter
    /// without anyone shipping a release.
    chapter: z.string().trim().max(80).optional(),
    // --- Education ---
    // UNIFEMGA is a graduates' association, so this is part of who a member is.
    highestQualification: z.string().trim().max(80).optional(),
    institution: z.string().trim().max(150).optional(),
    courseOfStudy: z.string().trim().max(150).optional(),
    graduationYear: z.coerce
        .number()
        .int()
        .min(1950)
        .max(new Date().getFullYear() + 10)
        .optional(),
    // --- Employment ---
    employmentStatus: z.union([z.nativeEnum(EmploymentStatus), z.literal('')]).optional(),
    organisation: z.string().trim().max(150).optional(),
    jobTitle: z.string().trim().max(120).optional(),
    // --- Next of kin ---
    nextOfKin: nextOfKinSchema.partial().optional(),
});
/** Qualifications offered in the profile form. */
export const QUALIFICATIONS = [
    'SSCE / WAEC',
    'OND',
    'HND',
    'NCE',
    "Bachelor's degree",
    'PGD',
    "Master's degree",
    'PhD',
    'Professional certification',
    'Other',
];
export const bankDetailsSchema = z.object({
    bankName: z.string().trim().min(2).max(100),
    bankCode: z.string().trim().max(10).optional(),
    accountNumber: accountNumberSchema,
    accountName: z.string().trim().min(2).max(120),
    bvn: z.union([bvnSchema, z.literal('')]).optional(),
});
/**
 * KYC submission (§11).
 *
 * Collected in one payload but saved incrementally — the member portal saves a
 * draft on every step so a dropped connection never costs someone their work.
 */
const kycFieldsSchema = z.object({
    fullLegalName: nameSchema,
    dateOfBirth: dateOfBirthSchema,
    gender: z.nativeEnum(Gender),
    residentialAddress: z.string().trim().min(5, 'Enter your address.').max(255),
    city: z.string().trim().min(2).max(80),
    state: z.string().trim().min(2).max(80),
    lga: z.string().trim().min(2, 'Enter your Local Government Area.').max(80),
    employmentStatus: z.nativeEnum(EmploymentStatus),
    organisation: z.string().trim().max(150).optional(),
    jobTitle: z.string().trim().max(120).optional(),
    monthlyIncomeBand: z.string().trim().max(60).optional(),
    nextOfKin: nextOfKinSchema,
    bankDetails: bankDetailsSchema,
    identificationType: z.enum(['NIN', 'INTERNATIONAL_PASSPORT', 'DRIVERS_LICENCE', 'VOTERS_CARD']),
    identificationNumber: z.string().trim().min(5).max(32),
    declarationAccepted: z.literal(true, {
        errorMap: () => ({ message: 'You must confirm the declaration.' }),
    }),
});
/**
 * Full submission.
 *
 * The refinement lives here rather than on the object so kycDraftSchema can
 * still call .partial() — a draft is saved field by field and must not be
 * judged against rules for fields the member has not reached yet.
 */
export const kycSubmissionSchema = kycFieldsSchema.superRefine((data, ctx) => {
    // The number has to match the document type it was filed under. Checked
    // here rather than only in the browser, because this is the field an
    // officer cannot sanity-check by eye.
    const problem = validateIdentificationNumber(data.identificationType, data.identificationNumber);
    if (problem) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: problem,
            path: ['identificationNumber'],
        });
    }
});
/**
 * Drops empty strings so an untouched field reads as absent.
 *
 * A six-step form posts every field it knows about, including the ones the
 * member has not reached. `.partial()` makes them optional, but an empty
 * string is still a value: "" fails the date parser, the gender enum and the
 * identification format, so saving a draft from step one was rejected for
 * fields on step five. Recursive, because next of kin and bank details are
 * nested objects with the same problem.
 */
function omitEmptyStrings(value) {
    if (typeof value === 'string')
        return value.trim() === '' ? undefined : value;
    if (Array.isArray(value))
        return value.map(omitEmptyStrings);
    if (value && typeof value === 'object') {
        const entries = Object.entries(value)
            .map(([key, item]) => [key, omitEmptyStrings(item)])
            .filter(([, item]) => item !== undefined);
        return Object.fromEntries(entries);
    }
    return value;
}
/**
 * Partial save for the multi-step KYC form. Every field optional by design —
 * a draft is a record of progress, not a submission, and must never be judged
 * against rules for steps the member has not reached.
 */
export const kycDraftSchema = z.preprocess(omitEmptyStrings, kycFieldsSchema.partial().extend({
    nextOfKin: nextOfKinSchema.partial().optional(),
    bankDetails: bankDetailsSchema.partial().optional(),
    declarationAccepted: z.boolean().optional(),
}));
export const kycReviewSchema = z
    .object({
    decision: z.enum(['APPROVE', 'REJECT', 'REQUEST_CORRECTION']),
    /** Mandatory for anything other than approval — members are owed a reason (§12). */
    reason: z.string().trim().max(1000).optional(),
    internalNotes: z.string().trim().max(2000).optional(),
    /** Fields the member must fix, shown inline in their portal. */
    fieldsRequiringCorrection: z.array(z.string().max(60)).max(40).optional(),
})
    .refine((data) => data.decision === 'APPROVE' || Boolean(data.reason?.trim()), {
    message: 'A reason is required when rejecting or requesting corrections.',
    path: ['reason'],
});
export const documentUploadSchema = z.object({
    documentType: z.nativeEnum(DocumentType),
    fileName: z.string().trim().min(1).max(255),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'application/pdf'], {
        errorMap: () => ({ message: 'Upload a JPG, PNG, WebP or PDF file.' }),
    }),
    /** Bytes. 10 MB ceiling — generous for a phone photo of an ID card. */
    fileSize: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024, 'Files must be 10 MB or smaller.'),
});
export const memberSearchSchema = searchSchema.extend({
    status: z.nativeEnum(MemberStatus).optional(),
    kycStatus: z.nativeEnum(KycStatus).optional(),
    hasActiveLoan: z.coerce.boolean().optional(),
    hasOverdue: z.coerce.boolean().optional(),
    registeredFrom: z.coerce.date().optional(),
    registeredTo: z.coerce.date().optional(),
});
export const adminUpdateMemberSchema = z.object({
    memberId: uuidSchema,
    status: z.nativeEnum(MemberStatus).optional(),
    membershipNumber: z.string().trim().max(32).optional(),
    notes: z.string().trim().max(2000).optional(),
    /** Bars new borrowing until this date. Used after a default (§46). */
    borrowingSuspendedUntil: z.coerce.date().nullable().optional(),
});
//# sourceMappingURL=member.js.map